// Copyright (C) 2004-2022 Artifex Software, Inc.
//
// This file is part of MuPDF.
//
// MuPDF is free software: you can redistribute it and/or modify it under the
// terms of the GNU Affero General Public License as published by the Free
// Software Foundation, either version 3 of the License, or (at your option)
// any later version.
//
// MuPDF is distributed in the hope that it will be useful, but WITHOUT ANY
// WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
// FOR A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
// details.
//
// You should have received a copy of the GNU Affero General Public License
// along with MuPDF. If not, see <https://www.gnu.org/licenses/agpl-3.0.en.html>
//
// Alternative licensing terms are available from the licensor.
// For commercial licensing, see <https://www.artifex.com/> or contact
// Artifex Software, Inc., 1305 Grant Avenue - Suite 200, Novato,
// CA 94945, U.S.A., +1(415)492-9861, for further information.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export const mupdfView = {
    countPages: wrap("countPages"),
    createCookie: wrap("createCookie"),
    deleteCookie: wrap("deleteCookie"),
    deleteItem: wrap("deleteItem"),
    documentOutline: wrap("documentOutline"),
    documentTitle: wrap("documentTitle"),
    drawPageAnnotsAsPixmap: wrap("drawPageAnnotsAsPixmap"),
    drawPageAsPNG: wrap("drawPageAsPNG"),
    drawPageAsPixmap: wrap("drawPageAsPixmap"),
    drawPageContentsAsPixmap: wrap("drawPageContentsAsPixmap"),
    drawPageWidgetsAsPixmap: wrap("drawPageWidgetsAsPixmap"),
    freeDocument: wrap("freeDocument"),
    getPageAnnotations: wrap("getPageAnnotations"),
    getPageLinks: wrap("getPageLinks"),
    getPageSize: wrap("getPageSize"),
    getPageText: wrap("getPageText"),
    mouseDownOnPage: wrap("mouseDownOnPage"),
    mouseDragOnPage: wrap("mouseDragOnPage"),
    mouseMoveOnPage: wrap("mouseMoveOnPage"),
    mouseUpOnPage: wrap("mouseUpOnPage"),
    openDocumentFromBuffer: wrap("openDocumentFromBuffer"),
    openDocumentFromStream: wrap("openDocumentFromStream"),
    openStreamFromUrl: wrap("openStreamFromUrl"),
    search: wrap("search"),
    setEditionTool: wrap("setEditionTool"),
};
const worker = new Worker("/mupdf/mupdf-view-worker.js");
const messagePromises = new Map();
let lastPromiseId = 0;
mupdfView.ready = new Promise((resolve, reject) => {
    worker.onmessage = function (event) {
        const type = event.data[0];
        if (type === "READY") {
            mupdfView.wasmMemory = event.data[1];
            const methodNames = event.data[2];
            for (const method of methodNames)
                mupdfView[method] = wrap(method);
            worker.onmessage = onWorkerMessage;
            resolve();
        }
        else if (type === "ERROR") {
            const error = event.data[1];
            reject(new Error(error));
        }
        else {
            reject(new Error(`Unexpected first message: ${event.data}`));
        }
    };
});
function onWorkerMessage(event) {
    const [type, id, result] = event.data;
    if (type === "RESULT")
        messagePromises.get(id).resolve(result);
    else if (type === "READY")
        messagePromises.get(id).reject(new Error("Unexpected READY message"));
    else if (type === "ERROR") {
        const error = new Error(result.message);
        error.name = result.name;
        error.stack = result.stack;
        messagePromises.get(id).reject(error);
    }
    else
        messagePromises.get(id).reject(new Error(`Unexpected result type '${type}'`));
    messagePromises.delete(id);
}
// TODO - Add cancelation for trylater queues
function wrap(func) {
    return function (...args) {
        return new Promise(function (resolve, reject) {
            const id = lastPromiseId++;
            messagePromises.set(id, { resolve, reject });
            if (args[0] instanceof ArrayBuffer)
                worker.postMessage([func, id, args], [args[0]]);
            else
                worker.postMessage([func, id, args]);
        });
    };
}
mupdfView.setLogFilters = wrap("setLogFilters");
const wrap_openStreamFromUrl = wrap("openStreamFromUrl");
const wrap_openDocumentFromStream = wrap("openDocumentFromStream");
mupdfView.openDocumentFromUrl = function (url, contentLength, progressive, prefetch, magic) {
    return __awaiter(this, void 0, void 0, function* () {
        yield wrap_openStreamFromUrl(url, contentLength, progressive, prefetch);
        return yield wrap_openDocumentFromStream(magic);
    });
};
mupdfView.terminate = function () { worker.terminate(); };
const openedDoc = {
    opened: undefined,
    fingerprint: "",
};
export function drawPageAsImage(pdf, pageNum, dpi) {
    return __awaiter(this, void 0, void 0, function* () {
        const { fingerprint } = pdf;
        if (openedDoc.opened && openedDoc.fingerprint !== fingerprint) {
            mupdfView.freeDocument();
            openedDoc.opened = undefined;
            openedDoc.fingerprint = "";
        }
        if (!openedDoc.opened) {
            const buf = yield pdf._pdfDoc.getData();
            const docPtr = yield mupdfView.openDocumentFromBuffer(buf, fingerprint);
            openedDoc.fingerprint = fingerprint;
            openedDoc.opened = docPtr;
        }
        const size = yield mupdfView.getPageSize(pageNum);
        const renderCookie = yield mupdfView.createCookie();
        const destDpi = dpi * devicePixelRatio;
        const renderPromise = mupdfView.drawPageAsPixmap(pageNum, destDpi, renderCookie);
        const imageData = yield renderPromise;
        return imageData;
    });
}
export function drawPageAsPng(pdf, pageNum, dpi) {
    return __awaiter(this, void 0, void 0, function* () {
        const { fingerprint } = pdf;
        if (openedDoc.opened && openedDoc.fingerprint !== fingerprint) {
            mupdfView.freeDocument();
            openedDoc.opened = undefined;
            openedDoc.fingerprint = "";
        }
        if (!openedDoc.opened) {
            const buf = yield pdf._pdfDoc.getData();
            const docPtr = yield mupdfView.openDocumentFromBuffer(buf, fingerprint);
            openedDoc.fingerprint = fingerprint;
            openedDoc.opened = 1;
        }
        let imageData = new Uint8Array([0]);
        const size = yield mupdfView.getPageSize(pageNum);
        const renderCookie = yield mupdfView.createCookie();
        const destDpi = dpi * devicePixelRatio;
        const renderPromise = mupdfView.drawPageAsPNG(pageNum, destDpi, renderCookie);
        imageData = yield renderPromise;
        return imageData;
    });
}
//# sourceMappingURL=MupdfRenderer.js.map